import { useLocale } from '@/components/Link'
import useTenant from '@/hooks/useTenant'
import { getCountryFromLocale } from '@fusion/locale/parsing'
import { KeyRounded } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormHelperText,
  GridLegacy as Grid,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import HeaderRoundedIcon from '../headerRoundedIcon'
import PasswordGuidance from './passwordGuidance'
import PasswordInput from './PasswordInput'
import { getStrengthMap } from './passwordStrength'
import {
  getPasswordStrengthRules,
  passwordStrengthScore,
  passwordStrengthWarning,
  validateRules,
} from './rules'

type Props = {
  onSubmit?: (values: FormValues) => void | Promise<void>
  showHeaderIcon?: boolean
  titleText?: string
  subTitleText?: string
  requireCurrentPasswordInput?: boolean
  requireCodeInput?: boolean
  isSubmitting: boolean
  error?: string
  submitText?: string
  disabled?: boolean
  email?: string
  code?: string
  propsReady?: boolean
  showTerms?: boolean
}

export type ErrorsState = {
  username?: string[]
  authCode?: string[]
  password?: string[]
  confirmPassword?: string[]
  currentPassword?: string[]
}

export interface FormValues {
  username: string
  authCode: string
  password: string
  confirmPassword: string
  currentPassword?: string
  subscribeNewsletter: boolean
}

const INITIAL_STATE = {
  username: '',
  authCode: '',
  password: '',
  confirmPassword: '',
  currentPassword: '',
  subscribeNewsletter: false,
}

const ResetPasswordForm = (props: Props) => {
  const { t } = useTranslation()

  const {
    showHeaderIcon,
    titleText = '',
    submitText = 'Submit',
    subTitleText = '',
    requireCurrentPasswordInput = false,
    requireCodeInput = true,
    onSubmit = () => null,
    error,
    email = '',
    code = '',
    propsReady = true,
    showTerms = false,
  } = props

  const [errors, setErrors] = useState<ErrorsState>({})
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState({
    ...INITIAL_STATE,
    ...{ username: email, authCode: code },
  })
  const [termsAccepted, setTermsAccepted] = useState(false)

  const emailInputShouldBeVisible = useRef(false)
  const codeInputShouldBeVisible = useRef(false)

  const passwordStrengthRules = getPasswordStrengthRules(t)
  const strengthMap = getStrengthMap(t)

  const tenant = useTenant()
  const locale = useLocale()

  useEffect(() => {
    if (!propsReady) return
    const result = validateRules({ username: email, authCode: code }, requireCodeInput)
    const emailError = !result.success && result.errors.username
    const codeError = !result.success && result.errors.authCode
    emailInputShouldBeVisible.current =
      emailInputShouldBeVisible.current || Boolean(!email || (email && emailError))
    codeInputShouldBeVisible.current =
      codeInputShouldBeVisible.current || Boolean(!code || (code && codeError))
  }, [propsReady, email, code])

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      ...(email && { username: email }),
      ...(code && { authCode: code }),
    }))
  }, [email, code])

  useEffect(() => {
    const result = validateRules(formData, requireCodeInput)
    if (result.success) setErrors({})
    else setErrors(result.errors)
  }, [formData])

  const { username, authCode, password, currentPassword, confirmPassword, subscribeNewsletter } =
    formData

  const isValid = () => {
    const result = validateRules(formData, requireCodeInput)
    return result.success
  }

  const handleChange =
    (field: string) =>
    ({ currentTarget: { value } }: ChangeEvent<HTMLInputElement>) => {
      const valueToSet = field === 'subscribeNewsletter' ? !formData.subscribeNewsletter : value
      setFormData((prev) => ({ ...prev, [field]: valueToSet }))
    }

  const submit = (e: React.MouseEvent | React.FormEvent) => {
    setSubmitted(true)
    e.preventDefault()

    const necessaryTermsAccepted = showTerms ? termsAccepted : true
    if (isValid() && necessaryTermsAccepted) {
      void onSubmit({
        username: username.trim(),
        authCode,
        password,
        currentPassword,
        confirmPassword,
        subscribeNewsletter,
      })
    }
  }

  const showErrors = !isValid() && submitted
  const passwordStrengthResult = passwordStrengthScore(password)

  const shouldShowConfirmPassword =
    passwordStrengthResult >= 3 && !errors.password && !errors.currentPassword

  const customErrors = []
  if (error) customErrors.push(error)
  if (email && errors.username) customErrors.push(errors.username)
  if (code && errors.authCode) customErrors.push(errors.authCode)

  const currentCountry = locale && getCountryFromLocale(locale)
  const regionalMarketingLinks = tenant.marketing.links.find(
    (tenant) => tenant.countryCode === currentCountry,
  )

  return (
    <form onSubmit={submit}>
      {showHeaderIcon ? (
        <HeaderRoundedIcon>
          <KeyRounded sx={{ fontSize: '60px' }} color="primary" />
        </HeaderRoundedIcon>
      ) : null}

      <Stack spacing={2}>
        {titleText ? (
          <Typography variant="h2" textAlign="center">
            {titleText}
          </Typography>
        ) : null}

        {subTitleText ? <Typography>{subTitleText}</Typography> : null}

        {emailInputShouldBeVisible.current ? (
          <TextField
            fullWidth
            value={username}
            label="Email *"
            id="email"
            autoComplete="email"
            error={showErrors}
            helperText={showErrors ? errors.username?.join(', ') : ''}
            type="email"
            variant="outlined"
            onChange={handleChange('username')}
            data-dd-privacy="allow"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        ) : null}

        {requireCodeInput && codeInputShouldBeVisible.current ? (
          <TextField
            fullWidth
            value={authCode}
            label="Code *"
            id="code"
            error={showErrors}
            helperText={showErrors ? errors.authCode?.join(', ') : ''}
            type="code"
            variant="outlined"
            onChange={handleChange('authCode')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        ) : null}

        {requireCurrentPasswordInput ? (
          <PasswordInput
            htmlID="password"
            label="Current password *"
            value={currentPassword}
            showError={showErrors}
            errorText={showErrors ? errors.currentPassword?.join(', ') : ''}
            onChange={handleChange('currentPassword')}
            data-testid="current-password-input"
          />
        ) : null}

        <PasswordInput
          label={`${t('authentication:setPassword:newPassword')} *`}
          htmlID="new-password"
          newPasswordInput
          value={password}
          onChange={handleChange('password')}
          allowShowPassword
          data-testid="new-password-input"
        />

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <FormHelperText>
              {t('authentication:setPassword:requirementsHelperText')}
            </FormHelperText>
          </Grid>

          {passwordStrengthRules.map((rule) => (
            <PasswordGuidance
              key={rule.text}
              rule={rule}
              password={password}
              showErrors={showErrors}
            />
          ))}
        </Grid>

        <div
          style={{
            maxHeight: password ? '50px' : '0px',
            visibility: password ? 'visible' : 'hidden',
            opacity: password ? 1 : 0,
            transition: 'visibility 0s, opacity 0.2s linear, max-height 0.2s ease-in-out',
          }}>
          <Stack spacing={1} direction="row">
            <Typography fontSize={'small'} color={strengthMap[passwordStrengthResult].resultColor}>
              {strengthMap[passwordStrengthResult].label}
            </Typography>

            <Typography mb={0} fontWeight="medium" fontSize={'small'}>
              {passwordStrengthWarning(password) ? ' - ' : ''}
            </Typography>

            <Typography mb={0} fontWeight="medium" fontSize={'small'}>
              {passwordStrengthWarning(password)}
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={passwordStrengthResult * 25}
            color={strengthMap[passwordStrengthResult].color}
          />
        </div>

        {shouldShowConfirmPassword ? (
          <PasswordInput
            htmlID="confirm-password"
            label={`${t('authentication:setPassword:confirmPassword')} *`}
            newPasswordInput
            value={confirmPassword}
            showError={showErrors}
            errorText={showErrors ? errors.confirmPassword?.join(', ') : ''}
            onChange={handleChange('confirmPassword')}
            data-testid="confirm-password-input"
          />
        ) : null}

        {showTerms && (
          <Box textAlign="left">
            <Typography>
              <Trans
                i18nKey="authentication:setPassword.termsAndPrivacy"
                values={{ tenantName: tenant.name }}
                components={{
                  terms: regionalMarketingLinks?.terms ? (
                    <Link
                      href={regionalMarketingLinks.terms}
                      target="_blank"
                      rel="noopener noreferrer">
                      {t('authentication:setPassword.termsText')}
                    </Link>
                  ) : (
                    <span aria-hidden="true"></span>
                  ),
                  and:
                    !regionalMarketingLinks?.terms || !regionalMarketingLinks?.privacy ? (
                      <span>{t('authentication:setPassword.andText')}</span>
                    ) : (
                      <span aria-hidden="true"></span>
                    ),
                  privacy: regionalMarketingLinks?.privacy ? (
                    <Link
                      href={regionalMarketingLinks.privacy}
                      target="_blank"
                      rel="noopener noreferrer">
                      {t('authentication:setPassword:privacyText')}
                    </Link>
                  ) : (
                    <span aria-hidden="true"></span>
                  ),
                }}>
                {t('authentication:setPassword.termsAndPrivacy')}
              </Trans>
            </Typography>

            <Box flexDirection="row" display="flex" sx={{ alignItems: 'center' }}>
              <Checkbox
                checked={termsAccepted}
                onChange={() => setTermsAccepted((prev) => !prev)}
                data-testid="signup-agreement-checkbox"
              />

              <Typography fontWeight="medium" fontSize={'small'}>
                {t('authentication:setPassword:agree')}*
              </Typography>
            </Box>

            {submitted && !termsAccepted && (
              <>
                <Alert icon={false} severity="error">
                  Terms and Conditions acceptance required
                </Alert>
                <br />
              </>
            )}

            <Box flexDirection="row" display="flex" sx={{ alignItems: 'flex-start' }}>
              <Checkbox
                onChange={handleChange('subscribeNewsletter')}
                checked={formData.subscribeNewsletter}
                sx={{ paddingTop: 0 }}
              />

              <Typography fontWeight="medium" fontSize={'small'}>
                {t('authentication:setPassword:marketingHelperText')}

                {regionalMarketingLinks?.privacy ? (
                  <Typography
                    component="span"
                    fontSize={12}
                    color="text.secondary"
                    fontWeight={400}>
                    {' '}
                    <Trans
                      i18nKey="authentication:setPassword:marketingPromise"
                      components={{
                        privacy: (
                          <Link
                            href={regionalMarketingLinks.privacy}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                  </Typography>
                ) : null}
              </Typography>
            </Box>
          </Box>
        )}

        {customErrors.length > 0 && (
          <Alert icon={false} severity="error">
            {customErrors.map((error) => (
              <React.Fragment key={error.length}>
                {error}
                <br />
              </React.Fragment>
            ))}
          </Alert>
        )}

        <Button
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          onClick={submit}
          data-testId="confirm-reset-password-button">
          {submitText}
        </Button>
      </Stack>
    </form>
  )
}

export default ResetPasswordForm
